import { DataSet, notification } from 'choerodon-ui/pro/lib';
import { getAccessToken as getToken, getCurrentUser, getCurrentUserId, getCurrentLanguage } from 'utils/utils';
import intl from 'utils/intl';
import qs from 'qs';
import { requestGet } from '@common/utils/request.js';
import { getCurrentOrganizationId } from 'utils/utils';
import { getActiveTabKey, refreshTab, closeTab, updateTab, getInitialTabData, getTabFromKey, openTab } from 'utils/menuTab';
import { getEnvConfig } from 'hzero-front/lib/utils/iocUtils';

// 全屏样式修改说明： 常规结构（root元素的子元素），用.cbms-fullscreen选择器。非常规结构（不是root的后代元素，如Modal默认生成的dom结构）用.cbms-fullscreen-modal选择器
/**
 * @param Array<object>
 * [
 *  { meaning: key, value: value }
 * ]
 * @returns {
 *  key: value
 * }
 */
export const arrToObject = (arrayObject, meaning = 'meaning', value = 'value') => {
  if (!Array.isArray(arrayObject)) {
    console.warn('common模块中arrToObject方法需传入数组');
    return {};
  }
  const returnObj = {};
  arrayObject.forEach((item) => {
    returnObj[item[value]] = item[meaning];
  });
  return returnObj;
};
/**
 * @param Array<object>
 * [
 *  { meaning: key, value: value }
 * ]
 * @returns {
 *  key: value
 * }
 */
export const objectToArr = (obj, meaning = 'meaning', value = 'value') => {
  if (!(Object.prototype.toString.call(obj) === '[object Object]')) return [];
  return Object.keys(obj).map((i) => ({ [value]: i, [meaning]: obj[i] }));
};


/**
 * 根据图片地址获取base64
 */
export const imageUrlToBase64 = (imageUrl) =>{
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function() {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx!.drawImage(img, 0, 0);
      try {
        const base64Str = canvas.toDataURL('image/png');
        resolve(base64Str);
      } catch (error) {
        console.log('--cc--reject-=',error);
        reject();
      }
    };
    img.onerror = function(error) {
      console.log('--cc--onerror-=',error);
      reject();
    };
    img.src = imageUrl;
  });
}

/**
 * 海外印度私有化， 国内测试  某些接口访问swap
 */
export const isSwapServer = () => {
  return isOverseaIndBEMEnv();// || isTestApiEnv();
};

/**
 * 获取应用名字
 */
export const getWebAppName = () => {
  if (isOverseaIndBEMEnv()) {
    return 'Blue Energy Motors';
  }
  return intl.get('cbms.common.view.sanyiCBMS').d('三一锂能运营平台');
};


/**
 * 是否为 海外 印度BEM私有化
 */
export const isOverseaIndBEMEnv = () => {
  // 印度：https://eswap.blueenergyev.in/，https://eswap-api.blueenergyev.in/
  //  Google Map API Key：AIzaSyAq8HJxXDbHbelDFT2I_oP22QrYQx677s4
  return window.location.host?.includes('blueenergyev.in');
};


/**
 * 是否为海外环境
 */
export const isOverseaEnv = () => {
  return window.location.host?.includes('eswap');
};

/**
 * 是否为test环境
 */
export const isTestApiEnv = () => {
  return window.location.host?.includes('test') || getEnvConfig()?.['API_HOST'].includes('test');
};

/**
 * 私有云
 */
export const isSYCloudEnv = () => {
  return window.location.host?.includes('blueenergyev.in') ;
};

export const isNewPifu = () => {
  return isOverseaEnv() || isTestApiEnv();
};

// 根据 Array<object> 生成下拉框需要的dataset，
interface arrObjectType {
  meaning: string;
  value: string;
}
export const arrOrObjectToDataSet = (arrObjectOrObject: Array<arrObjectType> | object | (() => arrObjectType[])) => {
  let newData = [] as any;
  const readData =
    Object.prototype.toString.call(arrObjectOrObject) === '[object Function]' ? arrObjectOrObject() : arrObjectOrObject;
  if (Array.isArray(readData)) {
    newData = readData;
  } else if (Object.prototype.toString.call(readData) === '[object Object]') {
    newData = objectToArr(readData);
  }
  const data = {
    autoQuery: true,
    paging: false,
    data: newData,
  };
  return new DataSet(data);
};

// 提取数字项
export const extractNumbers = (obj) => {
  if (Array.isArray(obj)) {
    return obj
      .filter((item) => !isNaN(Number(item)) && Number(item) != Infinity && Number(item) != -Infinity)
      .map((one) => Number(one));
  }
  if (!isNaN(Number(obj)) && Number(obj) != Infinity && Number(obj) != -Infinity) {
    return Number(obj);
  }
  return undefined;
};

// 计算Y轴间距 取整100 整10
export const calYIntervalRound = (spanY, ySplitNumber?) => {
  const ySNumber = ySplitNumber || 4;
  let tmpInterval = Math.ceil(spanY / ySNumber);
  if (tmpInterval > 100) {
    return Math.ceil(tmpInterval / 100) * 100;
  } else if (tmpInterval > 10) {
    return Math.ceil(tmpInterval / 10) * 10;
  }
  return tmpInterval;
};

// 把 嵌套格式的树形结构 装换成
/**
 * [
 * {key: 1, title: '总部'， children: [...], disabled, selectable}
 * ]
 *
 */
export const transferTreeData = (treeData, config) => {
  const {
    keyString = 'id', // 原始数据key对应的字段
    newKeyString = 'key', // 转换后数据key对应的字段
    titleString = 'name', // 原始数据title对应的字段
    newTitleString = 'title', // 转换后数据 title对应的字段
    childrenString = 'sub', // 原始数据children对应的字段
    newChildrenString = 'children', // 转换后数据 children对应的字段
    disabledFn, // 是否禁用的判断函数，参数为单条数据
    selectableFn, // 是否可选的判断函数，参数为单条数据
  } = config;
  return (treeData || []).map((item) => {
    const obj = {
      ...item,
      [newKeyString]: item[keyString],
      [newTitleString]: item[titleString],
    };
    if (typeof disabledFn === 'function') {
      obj.disabled = disabledFn(item);
    }
    if (typeof selectableFn === 'function') {
      obj.selectable = selectableFn(item);
    }
    const children = item?.[childrenString];
    if (Array.isArray(children)) {
      obj[newChildrenString] = transferTreeData(item[childrenString], config);
    }
    return obj;
  });
};
/**
 * 数值单位转换（中英文自适应）
 *
 * 中文输出：1.23万 / 1.23亿 / 1.23万亿
 * 英文输出：12.3k / 123.4M / 1.2T
 *
 * @param value 原始数值
 * @returns { value: number; unit: string }
 */
export const changeUnit = (
  value: number,
): { value: number; unit: string } => {

  // 取绝对值用于判断，保留原符号
  const absValue = Math.abs(value);
  const sign = value < 0 ? -1 : 1;

  let newValue = absValue;
  let unit = '';

  // 定义阈值和对应的单位（中英文）
  const levels = getCurrentLanguage() === 'zh_CN'
    ? [
        { threshold: 10 ** 12, unit: '万亿', divisor: 10 ** 12 },
        { threshold: 10 ** 8, unit: '亿', divisor: 10 ** 8 },
        { threshold: 10 ** 4, unit: '万', divisor: 10 ** 4 },
      ]
    : [
        { threshold: 10 ** 12, unit: 'T', divisor: 10 ** 12 },
        { threshold: 10 ** 9, unit: 'B', divisor: 10 ** 9 },
        { threshold: 10 ** 6, unit: 'M', divisor: 10 ** 6 },
        { threshold: 10 ** 3, unit: 'k', divisor: 10 ** 3 },
      ];

  // 找到合适的单位级别
  let selectedLevel = null;
  for (const level of levels) {
    if (newValue >= level.threshold) {
      selectedLevel = level;
      break;
    }
  }

  if (selectedLevel) {
    newValue = newValue / selectedLevel.divisor;
    unit = selectedLevel.unit;
  } else {
    // 数值太小，不需要转换
    newValue = absValue;
    unit = '';
  }

  // 恢复符号并格式化数值（保留2位小数，去除末尾的0）
  newValue = sign * parseFloat(newValue.toFixed(2));

  return {
    value: newValue,
    unit,
  };
};
// 仅用于 MW、GW 的转换，
export const changeUnitM: (value: number) => { value: number; unit: string } = (value) => {
  const a1 = 10 ** 6;
  const a2 = 10 ** 9;
  let newValue = value;
  let newUnit = 'M';
  if (newValue >= a2 || newValue <= -1 * a2) {
    newUnit = `G`;
    newValue /= a2;
  } else {
    newUnit = `M`;
    newValue /= a1;
  }
  return {
    value: newValue,
    unit: newUnit,
  };
};

// 功率: kW MW GW
export const changeUnitKMGW: (value: number) => { value: number; unit: string } = (value) => {
  const a1 = 10 ** 3;
  const a2 = 10 ** 6;
  let newValue = value;
  let newUnit = 'kW';
  if (newValue >= a2 || newValue <= -1 * a2) {
    newUnit = `GW`;
    newValue /= a2;
  } else if (newValue >= a1 || newValue <= -1 * a1) {
    newUnit = `MW`;
    newValue /= a1;
  }
  return {
    value: newValue,
    unit: newUnit,
  };
};

// 电量: kWh、MWh、GWh
export const changeUnitKMGWh: (value: number) => { value: number; unit: string } = (value) => {
  const a1 = 10 ** 3;
  const a2 = 10 ** 6;
  let newValue = value;
  let newUnit = 'kWh';
  if (newValue >= a2 || newValue <= -1 * a2) {
    newUnit = `GWh`;
    newValue /= a2;
  } else if (newValue >= a1 || newValue <= -1 * a1) {
    newUnit = `MWh`;
    newValue /= a1;
  }
  return {
    value: newValue,
    unit: newUnit,
  };
};

// 仅用于 kWh、MWh、GWh的转换，
export const changeUnitK: (value: number) => { value: number; unit: string } = (value) => {
  const a1 = 10 ** 3;
  const a2 = 10 ** 6;
  let newValue = value;
  let newUnit = 'kWh';
  if (newValue >= a2 || newValue <= -1 * a2) {
    newUnit = `GWh`;
    newValue /= a2;
  } else if (newValue >= a1 || newValue <= -1 * a1) {
    newUnit = `MWh`;
    newValue /= a1;
  }
  return {
    value: newValue,
    unit: newUnit,
  };
};
// 去掉小数点后多余的0
export const decimalTem = (value) => {
  const regexp = /(?:\.0*|(\.\d+?)0+)$/;
  return value.replace(regexp, '$1');
};
type NumberConfig = {
  decimalPoint?: number; // 小数位数
  decimalPointAfterChangeUnit?: number; // 小数位数 转换单位后
  needChangeUnit?: any; // 单位转换 万、亿等
  useGrouping?: boolean; // 使用千分位分隔符
  noRenderNumber?: boolean; // 无需处理数据，仅把非数字返回称 ‘--’
  isRemoveDecimalZero?: boolean; // 是否去掉小数点后面多余的0
  changeUnitFnType?: 'mg' | 'kmg' | 'default'; // 单位转换函数类型，默认default常规，kmg：用于电量单位kWh、MWh、GWh的转换，
  returnType?: 'string' | 'object'; // 默认string 返回的数据格式（数据和数据单位是否分开，例：输入123456，默认string，返回'12.34万'，当为bject时，返回格式为 {number: 12.34, unit: '万'}）
  unit?;
};

// 保留2位小数 去除后面的0
export const genNumberNoTail = (num: any, dotAfter?: number) => {
  const tmpDotAfter = dotAfter || 2;
  const a = 10 ** tmpDotAfter;
  if (!isNaN(parseFloat(num))) {
    return (+parseFloat(num).toFixed(tmpDotAfter) * a) / a;
  }
  return '--';
};

type FnRenderNumber = (num: any, config?: NumberConfig) => string & { value: string; unit: string };
export const renderNumber: FnRenderNumber = (num: any, config?: NumberConfig) => {
  const {
    decimalPoint = 2,
    decimalPointAfterChangeUnit = 4,
    needChangeUnit,
    useGrouping = false,
    noRenderNumber = false,
    isRemoveDecimalZero = true,
    changeUnitFnType,
    returnType = 'string',
    unit = '',
  } = config || {};
  if (noRenderNumber) {
    return isNaN(num) ? '--' : num;
  }
  const newDecimalPoint = decimalPoint as any;
  let newNum = num;
  let newUnit = unit || '';
  if (typeof newNum === 'number' && !isNaN(newNum)) {
    if (needChangeUnit || changeUnitFnType) {
      if (needChangeUnit == 'kmgW') {
        const changeInfo = changeUnitKMGW(newNum);
        newNum = changeInfo.value;
        newUnit = changeInfo.unit;
      } else if (changeUnitFnType === 'mg') {
        const changeInfo = changeUnitM(newNum);
        newNum = changeInfo.value;
        newUnit = changeInfo.unit;
      } else if ('kmgWh' == needChangeUnit) {
        const transfInfo = changeUnitKMGWh(newNum);
        newUnit = `${transfInfo.unit}`;
        newNum = transfInfo.value;
      } else if ('kmg' == needChangeUnit) {
        const transfInfo = changeUnitK(newNum);
        newUnit = `${transfInfo.unit}`;
        newNum = transfInfo.value;
      } else if (needChangeUnit) {
        // 万 度
          const transfInfo = changeUnit(newNum);
          newUnit = `${transfInfo.unit}${getCurrentLanguage() === 'zh_CN' ? '': ' '}${unit}`;
          newNum = transfInfo.value;
      }
    }
    if (typeof newDecimalPoint === 'number' || !newDecimalPoint) {
      const newNum2 = newNum.toLocaleString('en-US', {
        minimumFractionDigits:
          isNaN(newDecimalPoint) && newUnit
            ? isNaN(decimalPointAfterChangeUnit)
              ? 4
              : decimalPointAfterChangeUnit
            : newDecimalPoint,
        maximumFractionDigits:
          isNaN(newDecimalPoint) && newUnit
            ? isNaN(decimalPointAfterChangeUnit)
              ? 4
              : decimalPointAfterChangeUnit
            : newDecimalPoint,
        useGrouping,
      });
      // isRemoveDecimalZero 为 false 时，0 也保留小数位（如 0.00），不再强制去零显示 "0"
      if (isRemoveDecimalZero) {
        return returnType === 'object' ? { value: decimalTem(newNum2), unit: newUnit } : decimalTem(newNum2) + newUnit;
      }
      return returnType === 'object' ? { value: newNum2, unit: newUnit } : newNum2 + newUnit;
    }
    return returnType === 'object'
      ? { value: newNum.toLocaleString('en-US', { useGrouping }), unit: newUnit }
      : newNum.toLocaleString('en-US', { useGrouping }) + newUnit;
  } else if (typeof newNum === 'string' && newNum.length > 0) {
    return isRemoveDecimalZero
      ? returnType === 'object'
        ? { value: decimalTem(newNum), unit: newUnit }
        : decimalTem(newNum)
      : newNum;
  }
  return changeUnitFnType === 'kmg' ? (returnType === 'object' ? { value: '--', unit: 'k' } : '--k') : '--';
};
// 生成一个校验小数点后指定位数小数的正则
export const regNumberAfterPoint = (length: number) => {
  const str = `^[0-9]+(\\.[0-9]{${Math.min(1, length)},${Math.max(1, length)}})?$`;
  return new RegExp(length === 0 ? `^[0-9]+$` : str);
};
export const objArrToObj = (objArr, valueCode = 'value', textCode = 'meaning') => {
  const obj = {};
  objArr.forEach((item) => {
    obj[item[valueCode]] = item[textCode];
  });
  return obj;
};
// 处理图片地址
/**
 * src 地址字符串
 * show 把后端传的图片地址处理成能显示的地址
 * upload 把后端传的图片地址处理成能显示的地址
 *
 * 显示所需格式  http://cbms-dev-api.sany.com.cn/basedata/v1/0/common-file/imagePreview?address=http://cbms-dev-minio.sany.com.cn/cbms-public/hpfm05/0/34d5bda4588b46619cd955c7d46dc7d9@logo.png&bucketName=user&token=85a2aa5c-c489-41c1-b4e4-c6d20f54d5e1
 *
 *    《===》
 * 传给后端所需格式 http://cbms-dev-minio.sany.com.cn/cbms-public/hpfm05/0/34d5bda4588b46619cd955c7d46dc7d9@logo.png
 *
 */
export const dealPictureSrc = (src: string, type: 'show' | 'upload') => {
  console.log('dealPictureSrc', src, type);
  if (type === 'upload') {
    return '';
  }
  const url = isOverseaEnv()? `/device/v1/${getCurrentOrganizationId()}/common-file/oss/client/preview` : `/device/v1/0/common-file/imagePreview?address=${src}&bucketName=user&token=${getToken()}`;
  if(isOverseaEnv()){
    const ossStr = `${process.env.API_HOST}${url}?key=${src}`;
    return ossStr;
}
  const newSrc = `${
    process.env.API_HOST
  }${url}`;
  return newSrc;
};
export const dealFileSrc = (src: string, type: 'show' | 'upload') => {
  let newSrc = `${
    process.env.API_HOST
  }/device/v1/0/common-file/uploadFile?address=${src}&bucketName=user&token=${getToken()}`;
  if (type === 'upload') {
    // 未完成
    newSrc = '';
  }
  // console.log('dealPictureSrc', src, type);

  return newSrc;
};
// 处理 查询表单的 导出参数
export const filterNoDataAttr = (params) => {
  if (!params) return {};
  const newParams = {} as any;
  Object.entries(params).forEach((item) => {
    if (!(item[1] === null || item[1] === undefined)) {
      const [key, value] = item;
      newParams[key] = value;
    }
  });
  return newParams;
};

// 临时 获取用户id(汉得自带的因未知原因加密了)
export const getUserId = () => {
  const handId = getCurrentUserId();
  if (isNaN(handId)) {
    const info = getCurrentUser();
    return info?.additionInfo?.userOrg?.userId;
  }

  return handId;
};
// 临时 获取企业id(汉得自带的因未知原因加密了)
export const getBusinessId = () => {
  const info = getCurrentUser();
  return info?.additionInfo?.businessCustomer?.id;
};
// 获取当前用户类型-(operator|company)
export const operator = 'operator'; // 运营商
export const company = 'company'; // 企业
export const getUserType = () => {
  const info = getCurrentUser();
  return info?.additionInfo?.businessCustomer?.type;
};
// 判断当前用户是否为租户，不是则为运营商
export const isTanant: () => boolean = () => {
  const info = getCurrentUser() || {};
  return !info.additionInfo?.businessCustomer;
};
// 判断当前账号的语言类型（zh_CN|en_US|）
export const getLanguageName = () => {
  const info = getCurrentUser();
  return info?.language;
};
const exportAxios = (downloadPath, params, name = '', callback?: any) => {
  return requestGet(downloadPath, {
    query: params,
    responseType: 'blob',
  })
    .then(async(res) => {
      if (!res) return;
      const resBlob = await res.blob();
      try {
        const blob = new Blob([resBlob as unknown as Blob], {
          type: 'application/octet-stream;charset=utf-8',
        });
        const resFileNameTemp = res.headers.get('content-disposition')?.match(/filename=(.+)/)[1].trim();
        const resFileName = resFileNameTemp? decodeURIComponent(resFileNameTemp).replace(/\.[^/.]+$/, '') : null;
        const fileName = `${name || resFileName || intl.get('cbms.common.view.expFile').d('导出文件')}.xlsx`;
        const elink = document.createElement('a');
        elink.download = fileName;
        elink.style.display = 'none';
        elink.href = URL.createObjectURL(blob);
        document.body.appendChild(elink);
        elink.click();
        URL.revokeObjectURL(elink.href);
        document.body.removeChild(elink);
      } catch (error) {
        notification.error({
          message: intl.get('cbms.common.view.failure').d('失败'),
          description: intl.get('cbms.common.view.expFileFail').d('文件导出失败'),
        });
      }
    })
    .finally(() => {
      // eslint-disable-next-line no-unused-expressions
      callback?.();
    });
};
// export const downloadFile = (downloadPath:[], fileName:[], querys:[]) => {
//   return exportAxios(downloadPath, fileName, querys);
// };
interface DownloadParams {
  downloadPath: string;
  fileName: string;
  params?: any;
  callback?: Function;
}
export const downloadFile = (downloadParams: DownloadParams[]) => {
  downloadParams.forEach((item: any) => {
    const downloadPath = item?.downloadPath;
    const params = item?.params;
    const fileName = item?.fileName;
    return exportAxios(downloadPath, params, fileName, item?.callback);
  });
};
export const getTitleByActiveKey = (tabs, activeKey) => {
  if (!tabs || !activeKey) {
    return null;
  }
  const obj = tabs.find((item) => item.key === activeKey);
  // console.log('aaaaaaa', tabs, obj);
  return obj?.title;
};
/**
 * 获取当前tab页签的菜单标题
 */
export const getCurrentTabTitle = (defaultTitle?: string | React.ReactNode) => {
  if (typeof defaultTitle === 'string' || !defaultTitle) {
    const { global = {} } = window.dvaApp._store.getState() || {};
    const { tabs, activeTabKey } = global || {};
    const title = getTitleByActiveKey(tabs, activeTabKey);
    return title;
  } else if (typeof defaultTitle === 'function') {
    return defaultTitle();
  }
  return defaultTitle;
};
// 地图用 生成marker的icon

const iconWidth = 30; // 站点图标  大小
const iconHeight = 30;
export const createIcon = (amp, params) => {
  const { image, width = iconWidth, height = iconHeight } = params || {};
  return new amp.Icon({
    size: new amp.Size(width, height),
    image,
    imageSize: new amp.Size(width, height),
    // imageOffset: new amp.Pixel(-9, -3),
  });
};
export const getIsBusiness = () => window.location.pathname?.includes('business');
type Config = {
  type?: 'session' | 'local'; // 是 sessionStorage或者localstorage，默认local
};
export const getItem: (key: string) => any = (key: string, config?: Config) => {
  const { type } = config || {};
  const item = type === 'session' ? sessionStorage.getItem(key) : localStorage.getItem(key);
  let res = {};
  if (item) {
    try {
      const itemObj = JSON.parse(item || '');
      res = itemObj;
    } catch {
      res = item;
    }
  }
  return res;
};
export const setItem = (key: string, value?: string | Object, config?: Config) => {
  const { type } = config || {};
  const Storage = type === 'session' ? sessionStorage : localStorage;
  let newVal: string = '';
  if (typeof value === 'string') {
    newVal = value;
  } else {
    try {
      const v = JSON.stringify(value);
      newVal = v;
    } catch {
      console.error('common模块setItem方法报错');
    }
  }
  // eslint-disable-next-line no-unused-expressions
  newVal ? Storage.setItem(key, newVal) : Storage.removeItem(key);
};
export const isFullScreenFn = () => {
  return Boolean(
    document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement ||
      document.webkitIsFullScreen
  );
};

export const exitFullscreen = () => {
  if (isFullScreenFn()) {
    const doc = document as any;
    if (document?.exitFullscreen) {
      return document.exitFullscreen();
    } else if (doc?.mozCancelFullScreen) {
      return doc.mozCancelFullScreen();
    } else if (doc?.webkitCancelFullScreen) {
      return doc.webkitCancelFullScreen();
    } else if (doc?.msExitFullScreen) {
      return doc.msExitFullScreen();
    }
    return Promise.reject();
  }
  return Promise.reject();
};
const layoutTypeClassNameMap = {
  horizontal: '.hzero-layout',
  side: '.hzero-side-layout-container',
  'side-all': '.hzero-common-layout-container',
  inline: '.hzero-normal-container',
};

export const toFullscreen = () => {
  // e.stopPropagation();
  // const { global = {} } = window.dvaApp._store.getState() || {};
  // const { tabs, activeTabKey } = global || {};
  // this.clearContextMenuVisible();
  const { menuLayout } = getCurrentUser() || {};
  if (!isFullScreenFn()) {
    const contentClass = layoutTypeClassNameMap[menuLayout];
    const node = document.querySelector(contentClass) || document.querySelector(`#root`)!.firstElementChild;
    node.classList.add('hzero-fullscreen');
    const nodeModal = document.querySelector(`.c7n-pro-modal-container`);
    if (nodeModal) {
      nodeModal.classList.add('cbms-fullscreen-modal');
    }
    if (document.documentElement.requestFullscreen) {
      return document.documentElement.requestFullscreen();
    } else if (document.documentElement.msRequestFullscreen) {
      return document.documentElement.msRequestFullscreen();
    } else if (document.documentElement.mozRequestFullScreen) {
      return document.documentElement.mozRequestFullScreen();
    } else if (document.documentElement.webkitRequestFullscreen) {
      return document.documentElement.webkitRequestFullscreen();
    } else {
      notification.info({
        // message: intl
        //   .get('hzero.common.message.notSupportFullScreen')
        //   .d('当前浏览器不支持全屏，请更换浏览器'),
        message: intl.get('hzero.common.message.confirm.title').d('提示'),
        description: intl.get('hzero.common.message.notSupportFullScreen').d('当前浏览器不支持全屏，请更换浏览器'),
      });
      return Promise.reject();
    }
  }
  // if (pane.key !== activeTabKey) {
  //   openTab(getTabFromKey(pane.key));
  // }·
  return Promise.reject();
};
/**
 *
 * @param boo : true-全屏，false:退出全屏，不传：切换
 * @returns Promisr
 */
export const fullScreenToggle = (boo?: Boolean) => {
  if (boo === true) {
    return toFullscreen();
  }
  if (boo === false) {
    return exitFullscreen();
  }
  if (isFullScreenFn()) {
    return exitFullscreen();
  }
  return toFullscreen();
};

// 计算echart的Y坐标值间隔
interface EYConfig {
  /** 整体同 yAxis */
  splitNumber?: number;
  max: number;
  min: number;
}
interface EYConfigResult {
  /** 整体同 yAxis */
  interval: number;
  max: number;
  min: number;
}
export const getEchartYData: (config?: EYConfig) => EYConfigResult = (config?: EYConfig) => {
  const { splitNumber = 4, max = 10, min = 0 } = config || {};
  const rMax = typeof max === 'number' || typeof max === 'string' ? Number(max) : 10;
  const rMin = typeof min === 'number' || typeof min === 'string' ? Number(min) : 0;
  const diff = rMax - rMin;
  // const maxNumberLevel = String(Math.floor(Math.max(Math.abs(rMax), Math.abs(rMin)) / splitNumber)).length;
  const maxNumberLevel = String(Math.floor(diff / splitNumber)).length; // 坐标轴数据测大小级别
  const n1 = 10 ** (maxNumberLevel - 1);
  const interval = Math.ceil((Math.ceil(diff / n1) * n1) / splitNumber) || 5;
  const newMax = interval * Math.ceil(rMax / interval);
  const newMin = rMin > 0 ? 0 : interval * Math.floor(rMin / interval);
  // console.log(`getEchartYData2, rMax :${rMax} , rMin :${rMin} , diff :${diff} , maxNumberLevel :${maxNumberLevel} , n1 :${n1} , interval :${interval}, newMax :${newMax}, newMin :${newMin}`);

  return {
    min: newMin,
    max: newMax,
    interval,
  };
};

export function parseContainerString(str) {
  // 检查字符串是否以'集装箱'开头
  if (!str?.startsWith(intl.get('cbms.common.view.jContainer').d('集装箱'))) {
    return str;
  }
  // 移除开头的'集装箱'
  const trimmedStr = str.substring(3);
  // 使用正则表达式匹配数字簇数字的格式
  const match = trimmedStr.match(/^(\d+)(簇\d+)?$/);
  if (match) {
    // 提取匹配到的数字
    const firstNumber = match[1];
    const secondNumber = match[2] ? match[2].substring(1) : '';
    // 将数字格式化为两位数
    const formattedFirstNumber = firstNumber.padStart(2, '0');
    const formattedSecondNumber = secondNumber && secondNumber.padStart(2, '0');
    // 拼接成C0112的格式
    return `C${formattedFirstNumber}${formattedSecondNumber}`;
  } else {
    return str; // 如果没有匹配到正确的格式
  }
}

export function convertToString(str) {
  if (!str?.startsWith('C')) {
    return str;
  }
  // 去除开头的'C'
  const withoutC = str.substring(1);
  // 确保我们至少有两个字符（一个数字），否则返回原始字符串或空字符串
  if (withoutC.length < 2) {
    return withoutC.length === 0 ? str : str; // 或者返回错误，取决于你的需求
  }
  // 提取集装箱编号和簇编号
  const containerNum = parseInt(withoutC.substring(0, 2), 10); // 集装箱编号始终为一位数字
  const clusterNum = withoutC.length > 2 ? withoutC.substring(2) : ''; // 簇编号是剩余的部分
  // 构建输出字符串
  return `${intl.get('cbms.common.view.jContainer').d('集装箱')}${containerNum}${
    clusterNum.length > 0 ? `簇${clusterNum}` : ''
  }`;
}

/**
 * 根据电站code获取币种
 */
export const g_getCurrencyByCode = (code) => {
  if (!code) {
    return '';
  }
  let tmpCurry;
  const tmpStr = sessionStorage.getItem('kStationCurrencyArr') || '';
  if (tmpStr) {
    const StationCurrencyArr = JSON.parse(tmpStr);
    tmpCurry = StationCurrencyArr.find((one) => code == one.code)?.currencyName;
  }
  if (!tmpCurry) {
    if (!isOverseaEnv()) {
      tmpCurry = intl.get('cbms.common.view.yuan').d('元');
    }
  }
  return tmpCurry||'';
};

/**
 * 根据电站code获取时区
 */
export const g_getTimeZoneByCode = (code) => {
  if (!isOverseaEnv()) {
    return 'GMT+08:00';
  }
  if (!code) {
    return '';
  }
  const tmpStr = sessionStorage.getItem('kStationCurrencyArr') || '';
  if (tmpStr) {
    const StationCurrencyArr = JSON.parse(tmpStr);
    return StationCurrencyArr.find((one) => code == one.code)?.timeZone || '';
  }
  return '';
};

/**
 * 返回操作列宽度
 * @param onum 操作按钮个数
 * @param zhCNWidth 中文宽度
 * @param enUSWidth 英文宽度
 * @returns 宽度
 */
export function getOperatorColumnWidth(
  onum: 1 | 2 | 3,
  zhCNWidth?: number,
  enUSWidth?: number
): number {
  const zhCNDefaultWidth = [100, 160, 180];
  const enUSDefaultWidth = [160, 300, 340];
  const currentLanguage = getCurrentLanguage();

  if (currentLanguage === 'zh_CN') {
    if (zhCNWidth) return zhCNWidth;
    return zhCNDefaultWidth[onum - 1];
  }
  if (enUSWidth) return enUSWidth;
  return enUSDefaultWidth[onum - 1];
}

/**
 * 使用openTab汉德自带的跳转,替代history.push
 * @param params 同openTab的参数
 * 在openTab的基础上优化了
 * 1. 可以不传params.key，使用params.path。
 * 如不传parmas.key则不要在params.path后面拼参数，参数通过search和state传。
 * 2. search参数支持对象和字符串两种形式。
 */
export const historyPush = async(params)=>{
  if(params?.search){
    // eslint-disable-next-line no-param-reassign
    params.search = typeof params.search === 'string' ? params.search : qs.stringify({
      ...params.search,
    });
  }
  await closeTab(params.path || params.key);
  await openTab({
    ...params,
    key: params.key || params.path,
    path: params.path,
  });
};

export { operatorRender } from "./renderer";
